import { useCallback, useEffect, useState } from "react";

// Configuración del optimizador (M10). Los valores son editables por
// el admin desde un panel arriba de los tabs y se persisten en
// sessionStorage para que naveguen entre páginas sin perder los
// settings (cada sesión arranca fresca igual).
//
// Defaults pensados como "operación típica de un día de Lima":
//   - 12h jornada
//   - 125% peso permitido (admite sobrecarga del 25%)
//   - 100% volumen (no sobre-cubicar)
//   - 0.1 kg / S/ y 0.001 m³ / S/ como proxy cuando el CSV no trae
//     columnas reales de peso/volumen

export type OptimizerConfig = {
  /** Horas máximas por transportista (hard limit, siempre activo). */
  maxHours: number;
  /** % del peso máximo del vehículo permitido (125 = 25% sobrecarga). */
  maxWeightPct: number;
  /** % del volumen máximo del vehículo permitido. */
  maxVolumePct: number;
  /** Proxy de peso por sol cuando el CSV no trae la columna. */
  kgPerSol: number;
  /** Proxy de volumen por sol cuando el CSV no trae la columna. */
  m3PerSol: number;
  /** Si false, el optimizador ignora la restricción de peso. */
  useWeight: boolean;
  /** Si false, el optimizador ignora la restricción de volumen. */
  useVolume: boolean;
  /** Si true, el optimizador balancea ventas (S/) entre carriers. Sin
   *  cap (no es un recurso limitado), solo objetivo de balance. */
  useSales: boolean;
  /** Minutos de servicio por cliente (default 10). M10.6 traerá lookup
   *  per cliente por business_type; hoy es global. */
  serviceMinutes: number;
  /** Penalty por cada cliente reasignado a un chofer distinto al del
   *  CSV. 0 = optimiza puro (puede cambiar todo). 100 = moderado.
   *  1000+ = preserva la semilla salvo cambios necesarios. */
  stabilityPenalty: number;
};

export const DEFAULT_OPTIMIZER_CONFIG: OptimizerConfig = {
  maxHours: 12,
  maxWeightPct: 125,
  maxVolumePct: 100,
  kgPerSol: 0.1,
  m3PerSol: 0.001,
  useWeight: true,
  useVolume: true,
  useSales: false, // opt-in: el operador decide si quiere equiparar facturación
  serviceMinutes: 10,
  // 1000 = escala comparable al costo de los arcs (1km ≈ 1000 unidades).
  // Bajo 1000 es imperceptible. Por encima de 10000 fuerza preservación
  // total a costa de balance.
  stabilityPenalty: 1000,
};

const STORAGE_KEY = "micelio.programacion.config.v1";

function loadFromStorage(): OptimizerConfig {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_OPTIMIZER_CONFIG;
    const parsed = JSON.parse(raw) as Partial<OptimizerConfig>;
    // Merge con defaults — si en el futuro agregamos campos nuevos,
    // los settings viejos no se rompen.
    return { ...DEFAULT_OPTIMIZER_CONFIG, ...parsed };
  } catch {
    return DEFAULT_OPTIMIZER_CONFIG;
  }
}

export function useOptimizerConfig(): [OptimizerConfig, (next: Partial<OptimizerConfig>) => void, () => void] {
  const [config, setConfig] = useState<OptimizerConfig>(() => loadFromStorage());

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch {
      // sessionStorage puede fallar en modo privado / quota — silencioso.
    }
  }, [config]);

  const patch = useCallback((next: Partial<OptimizerConfig>) => {
    setConfig((prev) => ({ ...prev, ...next }));
  }, []);

  const reset = useCallback(() => {
    setConfig(DEFAULT_OPTIMIZER_CONFIG);
  }, []);

  return [config, patch, reset];
}
